/* eslint-disable i18next/no-literal-string */
import React, { useState, useEffect, useRef } from 'react';
import { ReactComponent as Grid } from 'assets/images/general-landing/ai-video/grid.svg';
import { ReactComponent as Title1 } from 'assets/images/general-landing/ai-video/title-1.svg';
import { ReactComponent as Title2 } from 'assets/images/general-landing/ai-video/title-2.svg';
import centerImg from 'assets/images/general-landing/ai-video/center.jpg';
import threeDRenderVideo from 'assets/images/floor-plan-landing/3d-render.mp4';
import styles from './AIVideography.module.scss';

export default function AIVideography() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 1000);
    };

    const handleScroll = () => {
      if (!wrapperRef.current || isMobile) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate progress based on when the component enters viewport
      const startProgress = Math.min(1, Math.max(0, (windowHeight - rect.top - 200) / windowHeight));

      // Use a combined progress that goes from 0 to 1 as we scroll through the component
      const progress = Math.min(1, startProgress * 1.5);
      setScrollProgress(progress);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  // Calculate image properties based on scroll progress
  const getImageStyle = () => {
    // If mobile, return static styles without animation
    if (isMobile) {
      return {
        width: 109,
        height: 80,
        borderRadius: 12,
        overflow: 'hidden',
      };
    }

    const progress = Math.min(1, Math.max(0, scrollProgress));

    // Get container dimensions for proper scaling within section
    const containerWidth = wrapperRef.current?.offsetWidth || 1200;

    // Starting values (full container size)
    const startWidth = containerWidth;
    const startHeight = (containerWidth / 224) * 164;

    // Ending values (final position)
    const endWidth = 224;
    const endHeight = 164;

    // Interpolate between start and end values
    const currentWidth = startWidth + (endWidth - startWidth) * progress;
    const currentHeight = startHeight + (endHeight - startHeight) * progress;

    return {
      position: progress < 1 ? 'absolute' : 'static',
      top: 0,
      left: progress < 1 ? '50%' : 'auto',
      transform: progress < 1 ? 'translate(-50%, 0)' : 'none',
      zIndex: progress < 1 ? 10 : 'auto',
      width: currentWidth,
      height: currentHeight,
      borderRadius: 12 * (currentWidth / endWidth),
      overflow: 'hidden',
    };
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.background}>
        <Grid />
      </div>
      <div className={styles.content}>
        <div className={styles.title} style={{ gap: 24 }}>
          <Title1 height={isMobile ? 64 : 164} />
          <div
            style={{
              width: isMobile ? 109 : 224,
              height: isMobile ? 80 : 164,
            }}
          >
            <img
              src={centerImg}
              alt="center"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                ...getImageStyle(),
              }}
            />

          </div>
          <Title2 height={isMobile ? 64 : 164} />
        </div>
        <div className={styles.subtitle}>
          Our cutting-edge feature that transforms static property images into dynamic, high-quality videos
        </div>

        <video src={threeDRenderVideo} alt="pricing" controls autoPlay muted loop />
      </div>
    </div>
  );
}
